import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./AssistanteDashboard.css";
import EditUserModal from "./EditUserModal";
import DocumentModal from "../../Forms/PdfForm/DocumentModal";
import AssistanteCreateCommercialForm from "../../auth/ConnexionForm/AssistanteCreateCommercial";
import UserProfileModal from "./UserProfileModal";
import ConcessionProfileModal from "./ConcessionProfileModal";
import Header from "../../common/Header";
import Footer from "../../common/Footer";
import PageWrapper from "../../common/PageWrapper";
import PropTypes from "prop-types";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import VendreEnLot from '../Modale/VendreEnLot';

const AssistanteDashboard = ({ menuState }) => {
  const API_URL = useMemo(() => process.env.REACT_APP_API_URL || "http://localhost:5002/api", []);
  const { isMenuOpen, setIsMenuOpen, onMenuToggle } = menuState || {};
  const [activeTab, setActiveTab] = useState("vehicules");
  const [isEditUserFormOpen, setIsEditUserFormOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedVehiculeId, setSelectedVehiculeId] = useState(null);
  const [isCreateCommercialFormOpen, setIsCreateCommercialFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userData, setUserData] = useState(null);
  const [concessions, setConcessions] = useState([]);
  const [selectedService, setSelectedService] = useState("All");
  const [selectedConcession, setSelectedConcession] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedConcessionProfile, setSelectedConcessionProfile] = useState(null);
  const [showConcessionProfile, setShowConcessionProfile] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersFilters, setUsersFilters] = useState({
    siret: "",
    nom: "",
    prenom: "",
    ville: "",
    service: "",
  });
  const [vehicules, setVehicules] = useState([]);
  const [filteredVehicules, setFilteredVehicules] = useState([]);
  const [vehiculesFilters, setVehiculesFilters] = useState({
    statut: "",
    numero_dossier: "",
    numero_chassis: "",
    vendeur: "",
    nom_client: "",
  });
  const [selectedVehicules, setSelectedVehicules] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showVendreEnLotModal, setShowVendreEnLotModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        ...options.headers,
      };
      const response = await fetch(url, { ...options, headers });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error(`API request failed: ${url}`, error);
      throw error;
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/users/me`);
      setUserData(data);
      setSelectedConcession(parseInt(data.concessions_id) || 0);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Erreur lors du chargement de votre profil. Veuillez rafraîchir la page.");
    }
  }, [API_URL, fetchWithAuth]);

  const fetchOptions = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/concessions`);
      const filteredConcessions = data.filter(
        (concession) => concession.groupe_id == userData?.groupe_id
      );
      setConcessions(filteredConcessions);
    } catch (error) {
      console.error("Error fetching concessions:", error);
      setError("Erreur lors du chargement des concessions");
    }
  }, [API_URL, fetchWithAuth, userData?.groupe_id]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!userData) return;

      const [usersData, vehiculesData] = await Promise.all([
        fetchWithAuth(`${API_URL}/assistantes/commercials`),
        fetchWithAuth(`${API_URL}/vehicules`),
      ]);

      const validUsers = Array.isArray(usersData) ? usersData : [];
      const validVehicules = Array.isArray(vehiculesData)
        ? vehiculesData
        : Array.isArray(vehiculesData?.data)
          ? vehiculesData.data
          : [];

      setUsers(validUsers);
      setVehicules(validVehicules);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message || "Erreur lors du chargement des données");
      setVehicules([]);
      setFilteredVehicules([]);
    } finally {
      setIsLoading(false);
    }
  }, [API_URL, fetchWithAuth, userData]);

  const applyFilters = useCallback((service, concessionId) => {
    if (Array.isArray(users)) {
      let newFilteredUsers = users;

      if (concessionId && concessionId !== 0) {
        newFilteredUsers = newFilteredUsers.filter(
          (user) => user && Number(user.concessions_id) === concessionId
        );
      }

      if (service && service !== "All") {
        newFilteredUsers = newFilteredUsers.filter(
          (user) => user && user.service_principal === service
        );
      }

      setFilteredUsers(newFilteredUsers);
    } else {
      setFilteredUsers([]);
    }

    if (Array.isArray(vehicules)) {
      let newFilteredVehicules = vehicules;

      if (concessionId && concessionId !== 0) {
        newFilteredVehicules = newFilteredVehicules.filter(
          (vehicule) => vehicule && Number(vehicule.concession_id) === concessionId
        );
      }

      if (service && service !== "All") {
        newFilteredVehicules = newFilteredVehicules.filter(
          (vehicule) => vehicule && vehicule.etat === service
        );
      }

      setFilteredVehicules(newFilteredVehicules);
    } else {
      setFilteredVehicules([]);
    }
  }, [users, vehicules]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (userData) {
      fetchOptions();
      fetchData();
    }
  }, [userData, fetchOptions, fetchData, location]);

  useEffect(() => {
    applyFilters(selectedService, selectedConcession);
  }, [users, vehicules, selectedService, selectedConcession, applyFilters]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditUserFormOpen(true);
  };

  const handleOpenDocumentModal = (vehiculeId) => {
    setSelectedVehiculeId(vehiculeId);
    setIsDocumentModalOpen(true);
  };

  const handleUpdateUser = async (data, userId) => {
    try {
      const formattedData = {
        ...data,
        concessions_id: parseInt(data.concessions_id),
        groupe_id: userData.groupe_id
      };

      const response = await axios.put(`${API_URL}/users/${userId}`, formattedData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        fetchData();
        return { success: true };
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);

      if (error.response?.data) {
        const errorData = error.response.data;
        console.error("Détail de l'erreur:", errorData);

        if (errorData.errorType === 'DUPLICATE_EMAIL' ||
          errorData.errorType === 'DUPLICATE_TELEPHONE' ||
          errorData.field) {
          return {
            success: false,
            error: errorData
          };
        } else if (error.response?.status === 403) {
          return {
            success: false,
            error: {
              message: "Vous n'avez pas les permissions nécessaires pour modifier cet utilisateur",
              errorType: "FORBIDDEN"
            }
          };
        } else {
          return {
            success: false,
            error: errorData
          };
        }
      } else {
        return { 
          success: false, 
          error: { 
            message: error.message || "Erreur lors de la mise à jour de l'utilisateur", 
            errorType: "UNKNOWN_ERROR" 
          }
        };
      }
    }
  };

  const handleCreateCommercialApi = async (data) => {
    try {
      const formattedData = {
        ...data,
        concessions_id: parseInt(data.concessions_id),
        groupe_id: userData.groupe_id 
      };
      
      setIsLoading(true);
      
      const response = await axios.post(`${API_URL}/users`, formattedData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        fetchData(); 
        
        return { 
          success: true, 
          userData: response.data.user 
        };
      }
      
      return { success: false };
    } catch (error) {
      setIsLoading(false);
      console.error("Erreur lors de la création de l'utilisateur:", error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.errorType === 'DUPLICATE_EMAIL' || 
            errorData.errorType === 'DUPLICATE_TELEPHONE' ||
            errorData.field) {
          return {
            success: false,
            error: errorData
          };
        } else if (error.response.status === 403) {
          return {
            success: false,
            error: {
              message: "Vous n'avez pas les permissions nécessaires pour créer un utilisateur",
              errorType: "FORBIDDEN"
            }
          };
        } else {
          return {
            success: false,
            error: errorData
          };
        }
      } else {
        return { 
          success: false, 
          error: { 
            message: error.message || "Erreur lors de la création de l'utilisateur", 
            errorType: "UNKNOWN_ERROR" 
          }
        };
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.")) {
      return;
    }

    try {
      setIsLoading(true);
      await fetch(`${API_URL}/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setError(null);

      setUsers(prev => prev.filter(u => u.id !== userId));
      setFilteredUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      setError("Erreur lors de la suppression de l'utilisateur");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateVehicule = async (vehiculeId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir dupliquer ce véhicule ?")) {
      return;
    }

    try {
      setIsLoading(true);
      await fetch(`${API_URL}/vehicules/${vehiculeId}/duplicate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setError(null);
      fetchData();
    } catch (error) {
      console.error("Erreur lors de la duplication:", error);
      setError("Erreur lors de la duplication du véhicule");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpdateVehiculeStatus = async (vehiculeId, newStatus) => {
    try {
      const response = await axios.put(`${API_URL}/vehicules/${vehiculeId}/status`,
        { statut: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        const updateArray = (array) => array.map(v =>
          v.id === vehiculeId ? { ...v, statut: newStatus } : v
        );

        setVehicules(updateArray);
        setFilteredVehicules(prev => updateArray(prev));
        setError(null);
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      setError("Erreur lors de la mise à jour du statut du véhicule");
    }
  };

  const handleDeleteVehicule = async (vehiculeId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce véhicule ? Cette action est irréversible.")) {
      return;
    }

    try {
      setIsLoading(true);
      await fetch(`${API_URL}/vehicules/${vehiculeId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setError(null);

      setVehicules(prev => prev.filter(v => v.id !== vehiculeId));
      setFilteredVehicules(prev => prev.filter(v => v.id !== vehiculeId));

      if (selectedVehicules.includes(vehiculeId)) {
        setSelectedVehicules(prev => prev.filter(id => id !== vehiculeId));
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      setError("Erreur lors de la suppression du véhicule");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectVehicule = (vehiculeId) => {
    setSelectedVehicules((prev) =>
      prev.includes(vehiculeId)
        ? prev.filter((id) => id !== vehiculeId)
        : [...prev, vehiculeId]
    );
  };

  const handleSelectAll = () => {
    setSelectedVehicules(selectAll ? [] : vehicules.map((v) => v.id));
    setSelectAll((prev) => !prev);
  };

  const handleServiceChange = (e) => {
    const newService = e.target.value;
    setSelectedService(newService);
    applyFilters(newService, selectedConcession);
  };

  const handleConcessionChange = (e) => {
    const newConcession = parseInt(e.target.value);
    setSelectedConcession(newConcession);
    applyFilters(selectedService, newConcession);
  };

  const handleFilter = (type) => {
    if (type === "users") {
      let queryParams = [];
      Object.keys(usersFilters).forEach((key) => {
        if (usersFilters[key]) {
          queryParams.push(`${key}=${encodeURIComponent(usersFilters[key])}`);
        }
      });

      fetchData(`users?${queryParams.join("&")}`);
    } else if (type === "vehicules") {
      if (!Array.isArray(vehicules) || vehicules.length === 0) {
        setFilteredVehicules([]);
        return;
      }

      let filtered = vehicules;

      if (selectedConcession && selectedConcession !== 0) {
        filtered = filtered.filter(vehicule =>
          vehicule && Number(vehicule.concession_id) === selectedConcession
        );
      }

      if (selectedService && selectedService !== "All") {
        filtered = filtered.filter(vehicule =>
          vehicule && vehicule.etat === selectedService
        );
      }

      const { statut, numero_dossier, numero_chassis, vendeur, nom_client } = vehiculesFilters;

      const hasFilters = statut ||
        numero_dossier.trim() ||
        numero_chassis.trim() ||
        vendeur.trim() ||
        nom_client.trim();

      if (!hasFilters) {
        setFilteredVehicules(filtered);
        return;
      }

      const result = filtered.filter(vehicule => {
        if (!vehicule) return false;

        const includes = (text, search) =>
          (text || "").toLowerCase().includes((search || "").toLowerCase().trim());

        return (
          (!statut || vehicule.statut === statut) &&
          (!numero_dossier.trim() || includes(vehicule.numero_dossier, numero_dossier)) &&
          (!numero_chassis.trim() || includes(vehicule.veh_6, numero_chassis)) &&
          (!vendeur.trim() || includes(vehicule.vendeur_nom, vendeur)) &&
          includes(`${vehicule.occ_tit_3 || ""} ${vehicule.tit_3 || ""}`.trim(), nom_client)
        );
      });

      setFilteredVehicules(result);
    }
  };

  const handleOpenUserProfile = (user) => {
    setSelectedUser(user);
    setShowUserProfile(true);
  };

  const handleCloseUserProfile = () => {
    setShowUserProfile(false);
  };

  const handleViewVehicule = (vehiculeId) => {
    navigate(`/vehicule/${vehiculeId}`);
  };
  
  const handleEditVehicle = (vehicleId) => {
    navigate(`/create-vehicule/${vehicleId}`);
  };

  const createCommercialFormComponent = (
    <AssistanteCreateCommercialForm
      show={isCreateCommercialFormOpen}
      onClose={() => {
        setIsCreateCommercialFormOpen(false);
      }}
      onSubmit={async (formData) => {
        const result = await handleCreateCommercialApi(formData);
        return result; 
      }}
      user={userData}
    />
  );

  const findConcessionById = (concessionId) => {
    return concessions.find(c => c.id === parseInt(concessionId));
  };

  const handleOpenConcessionProfile = (concession) => {
    setSelectedConcessionProfile(concession);
    setShowConcessionProfile(true);
  };

  const handleCloseConcessionProfile = () => {
    setShowConcessionProfile(false);
  };

  const getSelectedFolderNames = () => {
    if (selectedVehicules.length === 0) return [];
    return selectedVehicules.map(vehicleId => {
      const vehicle = vehicules.find(v => v.id === vehicleId);
      return vehicle?.numero_dossier || '';
    }).filter(Boolean);
  };

  return (
    <div className="admin-dashboard-wrapper">
      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        onMenuToggle={onMenuToggle}
      />

      <PageWrapper isMenuOpen={isMenuOpen}>
        <div className="admin-dashboard-content">
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === "vehicules" ? "active" : ""
                }`}
              onClick={() => setActiveTab("vehicules")}
            >
              Nos véhicules
            </button>
            <button
              className={`tab-button ${activeTab === "users" ? "active" : ""}`}
              onClick={() => setActiveTab("users")}
            >
              Mon équipe
            </button>
          </div>
          <div className="tabs">
            {/* Sélection de la concession */}
            <div className="flex flex-col">
              <label htmlFor="concession" className="font-semibold">
                Concession
              </label>
              <select
                id="concession"
                value={selectedConcession}
                onChange={handleConcessionChange}
                className="border p-2 rounded"
              >
                {concessions.map((concession) => (
                  <option key={concession.id} value={concession.id}>
                    {concession.nom}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélection du service */}
            <div className="flex flex-col">
              <label htmlFor="service" className="font-semibold">
                Service
              </label>
              <select
                id="service"
                value={selectedService}
                onChange={handleServiceChange}
                className="border p-2 rounded"
              >
                <option value="All">Tous</option>
                <option value="VO">VO</option>
                <option value="VN">VN</option>
                <option value="AV">AV</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Chargement des données...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button
                className="retry-button"
                onClick={() => {
                  setError(null);
                  fetchData();
                }}
              >
                Réessayer
              </button>
            </div>
          ) : (
            <>
              {activeTab === "users" && (
                <section className="admin-team-section">
                  <div className="admin-section-header">
                    <h2>Mon équipe</h2>
                    <div className="admin-actions-cell">
                      <button className="admin-create-button" onClick={() => setIsCreateCommercialFormOpen(true)}>
                        Créer un utilisateur
                      </button>
                    </div>
                  </div>
                  <div className="assistante-search-filters">
                    <div className="assistante-filters-row">
                      <div className="assistante-filter-cell">
                        <label>NOM</label>
                        <input
                          type="text"
                          value={usersFilters.nom}
                          onChange={(e) =>
                            setUsersFilters({
                              ...usersFilters,
                              nom: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell">
                        <label>Prénom</label>
                        <input
                          type="text"
                          value={usersFilters.prenom}
                          onChange={(e) =>
                            setUsersFilters({
                              ...usersFilters,
                              prenom: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell">
                        <label>Ville</label>
                        <input
                          type="text"
                          value={usersFilters.ville}
                          onChange={(e) =>
                            setUsersFilters({
                              ...usersFilters,
                              ville: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="assistante-filters-row">
                      <div className="assistante-filter-cell">
                        <label>Service</label>
                        <input
                          type="text"
                          value={usersFilters.service}
                          onChange={(e) =>
                            setUsersFilters({
                              ...usersFilters,
                              service: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell">
                        <label>SIRET</label>
                        <input
                          type="text"
                          value={usersFilters.siret}
                          onChange={(e) =>
                            setUsersFilters({
                              ...usersFilters,
                              siret: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell actions">
                        <button
                          className="assistante-reset-button"
                          onClick={() => {
                            setUsersFilters({
                              siret: "",
                              nom: "",
                              prenom: "",
                              ville: "",
                              service: "",
                            });
                            applyFilters(selectedService, selectedConcession);
                          }}
                        >
                          Réinitialiser
                        </button>
                        <button
                          className="assistante-search-button"
                          onClick={() => handleFilter("users")}
                        >
                          Rechercher
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <table className="admin-data-table">
                      <thead>
                        <tr>
                          <th>CONCESSION</th>
                          <th>VILLE</th>
                          <th>IDENTIFIANT</th>
                          <th>PRÉNOM</th>
                          <th>NOM</th>
                          <th>STATUT</th>
                          <th>SERVICE</th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              {user.concession_nom ? (
                                <div className="tooltip-container">
                                  <button
                                    className="user-identifier-button"
                                    onClick={() => {
                                      const concessionObj = findConcessionById(user.concessions_id);
                                      if (concessionObj) {
                                        handleOpenConcessionProfile(concessionObj);
                                      } else {
                                        setError("Informations de la concession non disponibles");
                                      }
                                    }}
                                  >
                                    {user.concession_nom}
                                  </button>
                                  <span className="tooltip-text">Voir fiche concession</span>
                                </div>
                              ) : (
                                "Non assignée"
                              )}
                            </td>
                            <td>{user.ville}</td>
                            <td>
                              <div className="tooltip-container">
                                <button
                                  className="user-identifier-button"
                                  onClick={() => handleOpenUserProfile(user)}
                                >
                                  {user.identifier || `${user.firstName?.substring(0, 3)}${user.lastName?.substring(0, 3)}`}
                                </button>
                                <span className="tooltip-text">Voir profil</span>
                              </div>
                            </td>
                            <td>{user.firstName}</td>
                            <td>{user.lastName}</td>
                            <td>{user.statut}</td>
                            <td>{user.service_principal}</td>
                            <td>
                              <div className="tooltip-container">
                                <button
                                  className="admin-edit-button"
                                  onClick={() => handleEditUser(user)}
                                >
                                  ✏️
                                </button>
                                <span className="tooltip-text">Modifier</span>
                              </div>
                            </td>
                            <td>
                              <div className="tooltip-container">
                                <button
                                  className="admin-delete-button"
                                  onClick={() => handleDeleteUser(user.id)}
                                >
                                  ✖️
                                </button>
                                <span className="tooltip-text">Supprimer</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              {activeTab === "vehicules" && (
                <section className="admin-vehicules-section">
                  <div className="admin-section-header">
                    <h2>Nos véhicules</h2>
                    <Link to="/create-vehicule">
                      <button className="admin-create-button">
                        Créer un véhicule
                      </button>
                    </Link>
                  </div>
                  <div className="assistante-search-filters">
                    <div className="assistante-filters-row">
                      <div className="assistante-filter-cell">
                        <label>Statut</label>
                        <select
                          name="statut"
                          value={vehiculesFilters.statut}
                          onChange={(e) =>
                            setVehiculesFilters({
                              ...vehiculesFilters,
                              statut: e.target.value,
                            })
                          }
                        >
                          <option value="">Tous</option>
                          <option value="Commande">Commande</option>
                          <option value="Stock">Stock</option>
                          <option value="Vendu">Vendu</option>
                          <option value="Demo">Démo</option>
                        </select>
                      </div>
                      <div className="assistante-filter-cell">
                        <label>N° de châssis</label>
                        <input
                          type="text"
                          value={vehiculesFilters.numero_chassis}
                          onChange={(e) =>
                            setVehiculesFilters({
                              ...vehiculesFilters,
                              numero_chassis: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell">
                        <label>N° de dossier</label>
                        <input
                          type="text"
                          value={vehiculesFilters.numero_dossier}
                          onChange={(e) =>
                            setVehiculesFilters({
                              ...vehiculesFilters,
                              numero_dossier: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="assistante-filters-row">
                      <div className="assistante-filter-cell">
                        <label>Vendeur</label>
                        <input
                          type="text"
                          value={vehiculesFilters.vendeur}
                          onChange={(e) =>
                            setVehiculesFilters({
                              ...vehiculesFilters,
                              vendeur: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell">
                        <label>Nom du client</label>
                        <input
                          type="text"
                          value={vehiculesFilters.nom_client}
                          onChange={(e) =>
                            setVehiculesFilters({
                              ...vehiculesFilters,
                              nom_client: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="assistante-filter-cell actions">
                        <button
                          className="assistante-reset-button"
                          onClick={() => {
                            setVehiculesFilters({
                              statut: "",
                              numero_dossier: "",
                              numero_chassis: "",
                              vendeur: "",
                              nom_client: "",
                            });
                            applyFilters(selectedService, selectedConcession);
                          }}
                        >
                          Réinitialiser
                        </button>
                        <button
                          className="assistante-search-button"
                          onClick={() => handleFilter("vehicules")}
                        >
                          Rechercher
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="admin-table-container">
                    <table className="admin-data-table">
                      <thead>
                        <tr>
                          <th>STATUT</th>
                          <th>NUMÉRO DE DOSSIER</th>
                          <th>NUMÉRO DE CHASSIS</th>
                          <th>NOM DU CLIENT</th>
                          <th>NOM DU VENDEUR</th>
                          <th>ACTIONS</th>
                          <th>
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="admin-checkbox"
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(filteredVehicules) && filteredVehicules.length > 0 ? (
                          filteredVehicules.map((vehicule) => (
                            vehicule ? (
                              <tr key={vehicule.id}>
                                <td>
                                  <select
                                    value={vehicule.statut || ""}
                                    onChange={(e) => handleUpdateVehiculeStatus(vehicule.id, e.target.value)}
                                    className="status-select"
                                    style={{ width: "fit-content" }}
                                  >
                                    <option value="Commande">Commande</option>
                                    <option value="Stock">Stock</option>
                                    <option value="Vendu">Vendu</option>
                                    <option value="Demo">Démo</option>
                                  </select>
                                </td>
                                <td>{vehicule.numero_dossier || "N/A"}</td>
                                <td>{vehicule.veh_6 || "N/A"}</td>
                                <td>{`${vehicule.occ_tit_3 || ""} ${vehicule.tit_3 || ""}`.trim() || "N/A"}</td>
                                <td>{vehicule.vendeur_nom || "N/A"}</td>
                                <td className="admin-actions-cell">
                                  <div className="tooltip-container">
                                    <button
                                      className="admin-view-button"
                                      onClick={() => handleViewVehicule(vehicule.id)}
                                      style={{ background: 'none', border: 'none', padding: '5px', cursor: 'pointer' }}
                                    >
                                      👁️
                                    </button>
                                    <span className="tooltip-text">Voir</span>
                                  </div>
                                  <div className="tooltip-container">
                                    <button
                                      className="admin-edit-button"
                                      onClick={() => handleEditVehicle(vehicule.id)}
                                    >
                                      ✏️
                                    </button>
                                    <span className="tooltip-text">Modifier</span>
                                  </div>
                                  <div className="tooltip-container">
                                    <button
                                      className="admin-download-button"
                                      onClick={() => handleOpenDocumentModal(vehicule.id)}
                                    >
                                      📥
                                    </button>
                                    <span className="tooltip-text">Télécharger</span>
                                  </div>
                                  <div className="tooltip-container">
                                    <button
                                      className="admin-duplicate-button"
                                      onClick={() => handleDuplicateVehicule(vehicule.id)}
                                    >
                                      📋
                                    </button>
                                    <span className="tooltip-text">Dupliquer</span>
                                  </div>
                                  <div className="tooltip-container">
                                    <button
                                      className="admin-delete-button"
                                      onClick={() => handleDeleteVehicule(vehicule.id)}
                                    >
                                      ✖️
                                    </button>
                                    <span className="tooltip-text">Supprimer</span>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedVehicules.includes(
                                      vehicule.id
                                    )}
                                    onChange={() =>
                                      handleSelectVehicule(vehicule.id)
                                    }
                                    className="admin-checkbox"
                                  />
                                </td>
                              </tr>
                            ) : null
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="no-data-message">
                              Aucun véhicule trouvé
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="admin-sell-button-container">
                    <button
                      className="admin-sell-button"
                      onClick={() => setShowVendreEnLotModal(true)}
                    >
                      Vendre en lot
                    </button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </PageWrapper>
      <Footer />
      {isEditUserFormOpen && selectedUser && (
        <EditUserModal
          show={isEditUserFormOpen}
          onHide={() => setIsEditUserFormOpen(false)}
          onSubmit={async (formData) => {
            const result = await handleUpdateUser(formData, selectedUser.id);
            if (result && result.success) {
              setIsEditUserFormOpen(false);
              setSelectedUser(null);
            }
            return result;
          }}
          user={selectedUser}
        />
      )}
      {isCreateCommercialFormOpen && createCommercialFormComponent}

      <DocumentModal
        show={isDocumentModalOpen}
        onHide={() => setIsDocumentModalOpen(false)}
        vehiculeId={selectedVehiculeId}
      />

      {showUserProfile && selectedUser && (
        <UserProfileModal
          show={showUserProfile}
          onHide={handleCloseUserProfile}
          user={selectedUser}
          onEdit={handleEditUser}
        />
      )}

      {showConcessionProfile && selectedConcessionProfile && (
        <ConcessionProfileModal
          show={showConcessionProfile}
          onHide={handleCloseConcessionProfile}
          concession={selectedConcessionProfile}
        />
      )}

      <VendreEnLot
        show={showVendreEnLotModal}
        onHide={() => setShowVendreEnLotModal(false)}
        selectedVehicles={selectedVehicules}
        onSuccess={() => {
          setSelectedVehicules([]);
          setSelectAll(false);
          fetchData();
        }}
        selectedVehiclesCount={selectedVehicules.length}
        numero_dossier={getSelectedFolderNames()}
      />
    </div>
  );
};

AssistanteDashboard.propTypes = {
  menuState: PropTypes.shape({
    isMenuOpen: PropTypes.bool,
    setIsMenuOpen: PropTypes.func,
    onMenuToggle: PropTypes.func,
  }),
};

export default AssistanteDashboard;
